import { ResultItem } from "./result-item"
import { ToggleSearch } from "./toggle-search"
import React, { FormEvent, useEffect, useRef, useState } from "react"
import lunr from "lunr"

type SearchBarProps = {
  documents: Array<Document>
  lunrIndex: lunr.Index
  entityName: string
}

type Document = {
  path: string
  title: string
}

export const SearchBar = ({
  documents,
  lunrIndex,
  entityName,
}: SearchBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef(null)
  const [userInput, setUserInput] = useState("")
  const [showResults, setShowResults] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(event.target.value)
  }

  const handleFormSubmission = (event: FormEvent) => {
    event.preventDefault()
  }

  let queryResult = searchDocuments(userInput)

  function searchDocuments(query: string) {
    let result: lunr.Index.Result[]

    // Handle search errors
    try {
      result = lunrIndex.search(query)
    } catch (error) {
      // Avoid breaking search when QueryParseError is thrown (special char input e.g ":")
      if (error instanceof lunr.QueryParseError) {
        // Remove special char from query
        let plainQuery = query.replace(/[^a-zA-Z0-9]/g, " ")
        result = lunrIndex.search(plainQuery)
      } else {
        result = []
      }
    }
    return result.map(item => {
      return documents.find(document => item.ref === document.path)
    })
  }

  function ToggleSearchBar() {
    if (searchOpen) {
      resetSearch()
    }
    setSearchOpen(!searchOpen)
  }

  useEffect(() => {
    if (userInput !== "") {
      setShowResults(true)
    } else {
      setShowResults(false)
    }
  }, [userInput])

  useEffect(() => {
    if (searchOpen) {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }
  }, [searchOpen])

  const closeSearchBar = () => {
    setUserInput("")
    setShowResults(false)
    setSearchOpen(false)
  }

  const resetSearch = () => {
    setUserInput("")
    setShowResults(false)
  }

  type MouseAndTouchEvents = MouseEvent | TouchEvent

  document.addEventListener("mousedown", function (event: MouseAndTouchEvents) {
    const inputEl = formRef?.current as HTMLInputElement | null
    if (!inputEl || inputEl.contains(event.target as Node)) {
      return
    }
    closeSearchBar()
  })

  document.addEventListener("keydown", function (event: KeyboardEvent) {
    if (event.keyCode === 27) {
      closeSearchBar()
    }
  })

  return (
    <div
      data-test="search__bar"
      className={`flex max-h-[44px] max-w-[500px] transition-all duration-300 bg-white rounded-[60px] ${
        searchOpen ? "grow-[1] border border-black-30" : "grow-[0.001]"
      }`}
      ref={formRef}
    >
      <ToggleSearch
        onClick={ToggleSearchBar}
        entityName={entityName}
        open={searchOpen}
      />
      <div
        data-test="search__container"
        className={`${searchOpen && "px-12 w-full"}`}
        aria-expanded={searchOpen}
        aria-labelledby={`${entityName}-search-toggle`}
        id={`${entityName}-search`}
      >
        <div className={`${entityName}-search w-full`}>
          <form
            onSubmit={handleFormSubmission}
            className="relative flex gap-12 items-center w-full"
            autoComplete="off"
          >
            <label htmlFor={`${entityName}-search-input`} className="sr-only">
              Search {entityName}
            </label>
            <input
              data-test="search__input"
              ref={inputRef}
              className={`${entityName}-search__input w-full transition-opacity duration-300 flex-1 text-14 focus:outline-none py-10 [&::-webkit-search-cancel-button]:hidden ${
                searchOpen ? "opacity-100" : "hidden opacity-0"
              }`}
              id={`${entityName}-search-input`}
              value={userInput}
              placeholder={`Search ${entityName.replace(/_/g, " ")}`}
              type="search"
              role="combobox"
              aria-autocomplete="list"
              aria-activedescendant="suggestion-0"
              onChange={handleInputChange}
            />
            <button
              type="submit"
              onClick={resetSearch}
              className={`transition-opacity duration-300 underline text-14 hover:font-medium focus:font-medium py-12 ${
                searchOpen ? "opacity-100" : "hidden opacity-0"
              }`}
            >
              Clear
            </button>
            <div
              data-test="search__results"
              aria-expanded={showResults}
              id={`${entityName}-search-suggestions`}
              className={`rounded-xl shadow-1 bg-white p-12 overflow-hidden absolute left-0 right-0 top-[44px] mt-10 z-40 ${entityName}-search__results
              ${showResults ? "block" : "hidden"}`}
            >
              <ul role="listbox" className="max-h-[259px] overflow-scroll">
                {queryResult.length === 0 && (
                  <li
                    className="border-t border-black-30 first:border-t-0"
                    role="option"
                    id="suggestion-0"
                  >
                    <span className="flex items-center text-14 truncate px-12 py-16">
                      No {entityName} found
                    </span>
                  </li>
                )}
                {queryResult.map((result, index) => {
                  if (result && userInput !== "") {
                    return (
                      <ResultItem
                        key={result.path}
                        title={result.title}
                        path={result.path}
                        index={index}
                      />
                    )
                  } else {
                    return null
                  }
                })}
              </ul>
            </div>
          </form>
        </div>
      </div>
      <button
        type="button"
        onClick={closeSearchBar}
        className={`transition-opacity duration-300 rounded-full border border-black-30 p-4 m-6 bg-white flex-block justify-center items-center self-center max-h-32 max-w-32 ${
          searchOpen ? "opacity-100" : "hidden opacity-0"
        }`}
      >
        <svg
          className="w-20 h-20"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M14.6516 4.16602L9.9974 8.82018L5.34323 4.16602L4.16406 5.34602L8.81906 9.99935L4.16406 14.6543L5.34323 15.8327L9.9974 11.1785L14.6516 15.8327L15.8316 14.6543L11.1766 9.99935L15.8316 5.34602L14.6516 4.16602Z"
            fill="#171A1B"
          />
        </svg>
      </button>
    </div>
  )
}
